import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * A document's identity in ONE column: the provider's own fiscal number on top, our AP-generated number
 * underneath as the link that opens it.
 *
 * <p>The two numbers are different things and both matter — a reconciler works from the paper invoice, which
 * only ever carries the fiscal number (#4788), while every internal action keys off ours. Giving each its own
 * table column costs horizontal space on tables that are already wide, and separates two facts that are read
 * together. Stacking them puts the number the user is searching for first and keeps the actionable one where
 * it can still be clicked.</p>
 *
 * <p>Tags and status icons belonging to the row are projected, so callers keep whatever badges they need
 * beside the number without this component knowing about any of them.</p>
 */
@Component({
  selector: 'app-document-identity',
  templateUrl: './document-identity.component.html',
})
export class DocumentIdentityComponent {
  /** Our AP-generated document number — the clickable one. Undefined is accepted as well as null: callers
   *  bind it straight from optional DTO fields, and both mean the same nothing here. */
  @Input({ required: true }) documentNumber: string | null | undefined = null;

  /** The provider's own printed/fiscal number (#4788). Absent on documents that carry none. */
  @Input() fiscalNumber?: string | null;

  /** Set false where there is nothing to open — the number then renders as plain text. */
  @Input() clickable = true;

  /** Tooltip for the link, e.g. "View document detail". */
  @Input() linkTitle?: string;

  @Output() documentClick = new EventEmitter<void>();

  /** With no fiscal number the AP number is the only identity there is, so it takes the primary styling. */
  get isPrimary(): boolean {
    return !this.fiscalNumber;
  }

  onDocumentClick(): void {
    if (this.clickable) this.documentClick.emit();
  }
}
